/* eslint-disable padding-line-between-statements */
import _ from 'lodash';
import TWIACoveragesHelper from "./TWIACoveragesHelper";

const CP_BPP_COV_DEDUCTIBLE_PATTERN_CODE = 'CPBPPCovDeductible';
const CP_BLDG_COV_DEDUCTIBLE_PATTERN_CODE = 'CPBldgCovDeductible';

// 2021-01-11 - Debra Frum - US11724 - Commercial Quote - Coverages Page - Builder's Risk
// Risk Item Type determines rounding rules for some coverage terms
/**
 * Some of the CovTerms are to be rounded. This object, contains the terms and the roundTo precision.
 * Note that entries with an empty riskItemType apply to specified coverage terms associated with all Coverable Risk Item Types
 * not explicitly specified in another entry that contains a non-empty riskItemType.
 * @type {({covTermType: [string, string, string, string], roundTo: number}|{covTermType: [string, string], roundTo: number})[]}
 */
const CP_COVTERMS_ROUNDING_RULESOBJ = [
    {
        riskItemType:
            [
                'DwelFRDwelAddiGreaterThan10Perc',
                'DwelFRDwelAddiLessThan10Perc',
                'DwelFRDwelNewConst',
                'DwelFRDwelRepairsNoAdditions'
            ],
        covTermType:
            [
                'DirectCPBldgCovLimitType'
            ],
        roundTo: 1
        // DE14594 - removed rounding so error message can show to user
    },
    {
        riskItemType: [''],
        covTermType:
            [
                'DirectTWCP_BldgCov_RCVType',
                'DirectTWCP_BldgCov_ACVType',
                'DirectTWCP_BPPCov_RCVType',
                'DirectTWCP_BPPCov_ACVType',
                'DirectCP_TWIA_BIEECov_IncomeDailyLimitType'],
        roundTo: 1
    }
    // DE11328 - removed auto rounding of limits
];

// 2021-01-27 - Debra Frum - US17869 - Commercial Quote - Coverages Page - Blanket Coverage
// CP risk item types that Blanket coverages can be added to
const CP_BLANKET_RISKITEMTYPES =
    [
        'Church',
        'LumberYard',
        'SchoolOrPrivateStruct',
        'SchoolOrPublicStruct'
    ];

// 2021-01-28 - Debra Frum - US17869 - Commercial Quote - Coverages Page - Blanket Coverage
// CP Building coverage patterns that Blanket coverage includes
const CP_BLANKET_BUILDING_COVERAGES =
    [
        'CPBldgCov',
        'CPBPPCov'
    ];

function getSourceDeductibleTermChosenOption(term) {
    const chosenOption = _.filter(term.options, o => o.code === term.chosenTerm);
    return (chosenOption.length === 1) ? chosenOption[0] : undefined;
}

function getDestinationDeductibleToSync(allCoverages, term) {
    const terms = _(allCoverages)
        .map('terms')
        .flatten()
        .filter(t => t.name === 'Deductible' && t.patternCode !== term.patternCode)
        .value();
    return terms.length === 1 ? terms[0] : undefined;
}

function getBldgCovDeductible(deductiblePatternCode, building) {
    const item = _(building.coverages)
        .map('terms')
        .flatten()
        .filter({name: 'Deductible', patternCode: deductiblePatternCode})
        .value();
    return item.length === 1 ? item[0] : undefined;
}

function syncDestinationDeductible(term, allCoverages) {

    const sourceDeductibleTermChosenOption = getSourceDeductibleTermChosenOption(term);
    const destinationDeductibleTermToSync = getDestinationDeductibleToSync(allCoverages, term);

    function getDestinationDeductibleOption() {
        const options = _.get(destinationDeductibleTermToSync, 'options');
        const optionName = _.get(sourceDeductibleTermChosenOption, 'name');
        if (options && optionName) {
            const option = _.filter(destinationDeductibleTermToSync.options, o => o.name === sourceDeductibleTermChosenOption.name);
            return option.length === 1 ? option[0] : undefined;
        }
        return undefined;
    }

    const destDeductibleTermOption = getDestinationDeductibleOption();
    if (destDeductibleTermOption !== undefined) {
        destinationDeductibleTermToSync.chosenTerm = destDeductibleTermOption.code;
        destinationDeductibleTermToSync.chosenTermValue = destDeductibleTermOption.name;
    }
}

/**
 * Ceil a number to the given precision
 * @param {number} num
 * @param {precision} precision Ex: 100, 1000
 * @returns {number}
 */
function ceilNumber(num, precision) {
    num = parseFloat(num);
    if (!precision) return num;
    return (Math.ceil(num / precision) * precision);
}

export default {

    /**
     * US11725 - Commercial Quote - Coverages Page - Building and Business Property
     * BuildingCoverage deductible term is to be the same for all buildings
     * @param {object} building UpdatedBuilding
     * @param {object} locations All Locations on the period.
     */
    syncBuildingCoverageDeductibleForAllBuildingsTwia(building, locations) {
        let updatedCovDeductibleTerm = getBldgCovDeductible(CP_BLDG_COV_DEDUCTIBLE_PATTERN_CODE, building);
        if (updatedCovDeductibleTerm === undefined) {
            updatedCovDeductibleTerm = getBldgCovDeductible(CP_BPP_COV_DEDUCTIBLE_PATTERN_CODE, building);
        }

        syncBuildingDeductiblesAcrossLocations();

        function syncBuildingDeductiblesAcrossLocations() {
            locations.buildings.forEach((bldg) => {
                if (building.publicID !== bldg.publicID) {
                    syncBuildingCoverageDeductibles(updatedCovDeductibleTerm, bldg.coverages);
                    // 2021-01-31 - Debra Frum - US17869 - Commercial Quote - Coverages Page - Blanket Coverage
                    // Blanket coverage deductible associated with the building coverage must also be the same
                    if (!_.isNil(bldg.blanket_TWIA)) {
                        syncBuildingCoverageDeductibles(updatedCovDeductibleTerm, bldg.blanket_TWIA.coverages);
                    }
                }
            });
        }

        function syncBuildingCoverageDeductibles(term, allCoverages) {
            const name = _.get(term, 'name');
            if (name !== 'Deductible') {
                return;
            }
            syncSourceDeductible();
            syncDestinationDeductible(term, allCoverages);

            function syncSourceDeductible() {
                const terms = _(allCoverages)
                    .map('terms')
                    .flatten()
                    .filter(t => t.name === 'Deductible' && t.patternCode === term.patternCode)
                    .value();
                const sourceDeductibleTermToSync = terms.length === 1 ? terms[0] : undefined;
                if (sourceDeductibleTermToSync === undefined) {
                    return;
                }
                sourceDeductibleTermToSync.chosenTerm = term.chosenTerm;
                sourceDeductibleTermToSync.chosenTermValue = term.chosenTermValue;
            }
        }
    },


    /**
     * US11725 - Commercial Quote - Coverages Page - Building and Business Property
     * BuildingCoverage Limit term rounded to 1000s
     * @param {coverage} coverage
     * @param {term} term
     */
    roundBuildingCoverageLimitsTwia(coverage, term, riskItemTypeTwia = null) {
        if (_.isNil(term)) {
            return;
        }

        // 2021-01-11 - Debra Frum - US11724 - Commercial Quote - Coverages Page - Builder's Risk
        // Risk Item Type determines the rounding rule for some coverage terms
        let covTermrule = null;
        // Look for a rule based on Risk Item Type and Coverage Term Type
        covTermrule = CP_COVTERMS_ROUNDING_RULESOBJ
            .filter(i => _.findIndex(i.riskItemType, r => r === riskItemTypeTwia) !== -1)
            .filter(j => _.findIndex(j.covTermType, t => t === term.type) !== -1);
        // Look for a rule based on just Coverage Term Type if a rule based on Risk Item Type and Coverage Term Type isn't found.
        if (_.isEmpty(covTermrule)) {
            covTermrule = CP_COVTERMS_ROUNDING_RULESOBJ
                .filter(i => _.findIndex(i.riskItemType, r => _.isEmpty(r)) !== -1)
                .filter(j => _.findIndex(j.covTermType, t => t === term.type) !== -1);
        }
        if (!_.isEmpty(covTermrule)) {
            term.directValue = ceilNumber(term.directValue, covTermrule[0].roundTo);
        }
    },

    /**
     * US11725 - Commercial Quote - Coverages Page - Building and Business Property
     * Deductibles should be same across BldCov and BPPCov
     * @param {object} term
     * @param {coverage} allCoverages
     */
    syncBuildingCoverageDeductiblesForCurrentBuildingTwia(term, allCoverages) {
        const termName = _.get(term, 'name');
        if (termName !== 'Deductible') {
            return;
        }
        syncDestinationDeductible(term, allCoverages);
    },

    containsValidationErrors_TWIA(errors) {
        const {issues, fieldIssues} = _.get(errors, 'validationIssues', {});
        if (!_.isEmpty(issues) || !_.isEmpty(fieldIssues)) {
            return true;
        }
        return false;
    },

    /**
     * US12404 For Commercial Policies determines the coverage form
     * note that all the buildings have the same type of coverage form
     * @param state
     * @param model
     * @returns {coverage form}
     */
    findCoverageForm_TWIA(state, model) {
        let coverageForm;
        // new building
        if (state.params.buildingID) {
            _.find(model.lobData.commercialProperty.coverables.locations.value, (location) => _.find(location.buildings, (building) => {
                    if (building.publicID === state.params.buildingID) {
                        coverageForm = building.coverageForm;
                    }
                }));
        } else {
            // existing building
            coverageForm = state.params.model.selectedBuilding.coverageForm;
        }

        return coverageForm;
    },

    // 2021-01-07 - Debra Frum - US11724 - Commercial Quote - Coverages Page - Builder's Risk
    /**
     * Determines if coverage terms meet the minimum validity criteria to allow syncing of the
     * given coverage
     * @param coverage
     * @returns {true} iff all coverage terms meet the minimum validity criteria to allow syncing
     *                 of the given coverage
     */
    termsValidForSync_TWIA(coverage, riskItemType) {
        if (coverage.terms && coverage.terms.length > 0) {
            // Sync if all terms are valid
            if (_.every(coverage.terms, aTerm => aTerm.aspects.valid)) {
                return true;
            }

            // 2021-01-07 - Debra Frum - US11724 - Commercial Quote - Coverages Page - Builder's Risk
            // Sync if only the Deductible term is invalid and its option list is empty.
            // Handles case where Deductible, which is a required field, is empty until the Limit
            // has been entered.  This case occurs for some Builder's Risk risk item types.
            // DE11476 - Both Deductible AND Coinsurance terms may be invalid and empty for certain
            // Builders' risk item types
            const invalidTerms = _.filter(coverage.terms, aTerm => !aTerm.aspects.valid);
            if (invalidTerms && invalidTerms.length <= 2) {
                const deductibleTerm = _.find(invalidTerms, {'name': 'Deductible'});
                if (deductibleTerm
                    && deductibleTerm.hasOptions === true
                    && (!deductibleTerm.options || deductibleTerm.options.length === 0)) {

                    if (invalidTerms.length === 1) {
                        return true;
                    } else if (_.find(invalidTerms, {'name': 'Coinsurance'})
                        && coverage.publicID === 'CPBldgCov'
                        && (riskItemType === 'DwelFRDwelAddiLessThan10Perc' || riskItemType === 'DwelFRDwelRepairsNoAdditions')) {
                        return true;
                    }
                }
            }
        }
        return false;
    },

    /**
     * US12404 - For Commercial Policies, determine if we should display
     * the msb link based on the coverage form and the particular coverage
     * @param coverage
     * @param coverageForm
     * @returns {true} iff there is match of coverage and coverage form
     */
    msbLink_TWIA(coverage, coverageForm) {
        let result = coverage.name === 'Building Coverage'
            && (coverageForm === 'TWCB' || coverageForm === 'TWCC' || coverageForm === 'TWCH');
        return result;
    },

    /**
     * DE11292 - Requirements Defect - Do Not Allow Multiple Buildings/Locations for Builder's Risk
     * Look for a builders risk building
     * @param location the location that has buildings
     * @returns true is builders risk items are found
     */
    locationHasBuildersRiskCoverageForm_TWIA(locations) {
        return locations && locations.find(location => {
            return location.buildings && location.buildings.find(building => {
                return building.coverageForm === 'TWCR';
            });
        });
    },
    
    // 2021-01-27 - Debra Frum - US17869 - Commercial Quote - Coverages Page - Blanket Coverage
    /**
     * Returns true if building meets conditions that allow a Blanket coverage to be added
     * @param building
     * @returns {boolean} true if building meets conditions that allow a Blanket coverage to be added
     */
    isBlanketAvailable_TWIA(building) {
        if (_.isUndefined(building.value.blanket_TWIA) && _.includes(CP_BLANKET_RISKITEMTYPES, building.riskItemType_TWIA.value.code)) {
            if ((building.coverages.value.filter(cov => cov.publicID === 'CPBldgCov' && cov.selected && cov.isValid).length >= 1) 
                && (building.coverages.value.filter(cov => cov.publicID === 'CPBPPCov' && cov.selected && cov.isValid).length >= 1)) {
                    return true;
            }
        }
        return false;
    },

    // 2021-01-28 - Debra Frum - US17869 - Commercial Quote - Coverages Page - Blanket Coverage
    /**
     * Configure Building coverages included in a Blanket coverage to be read-only when a Blanket coverage has been added
     * @param building
     */
    configBuildingCoveragesForBlanket_TWIA(building) {
        if (_.includes(CP_BLANKET_RISKITEMTYPES, building.riskItemType_TWIA.value.code)) {
            // 2021-05-03 - Debra Frum - DE11908 - Valuation Method with value 'Replacement Cost Coverage – Actual Cash Value Roofs'
            //                                     should be non editable
            // No longer assume that the building coverages included in a Blanket coverage are always editable when there
            // is no Blanket coverage.  Configuration will be restored from back-end when a Blanket coverage is removed.

            // Configure building coverages included in a Blanket coverage to be read-only
            if (!_.isUndefined(building.value.blanket_TWIA)) {
                building.coverages.value.map((c) => {
                    if (_.includes(CP_BLANKET_BUILDING_COVERAGES, c.publicID)) {
                        // 2021-04-22 - Debra Frum - US17869, DE11499
                        // After the Blanket coverage has been added, prevent building coverages included in a Blanket coverage
                        // from being de-selected by clicking the Blanket coverage name
                        c.required = true;
                        // After the Blanket coverage has been added, prevent building coverages included in a Blanket coverage
                        // from being modified
                        c.editable_TWIA = false;
                        c.terms.forEach(term => {
                            term.editable_TWIA = false;
                        });
                    }
                });
                // 2021-01-31 - Debra Frum - US17869 - Commercial Quote - Coverages Page - Blanket Coverage
                // Overwrite the coverages to trigger the watch in CoveragesUtil so that the UI will update
                building.coverages = _.cloneDeep(building.coverages);
            }
        }
    },

    /**
     * * US12611 - Commercial Renewal - Building Sub-flow - Coverages - TWCB
     * US12229 - Commercial Policy Change - Coverages
     * For BuildingAndBusinessPropertyCoverage
     *  if CovLimit < 80% of BuildingReplacement Coverage and ValuationMethod is ActualCashValue
     *      display - info message 'does not meet' criteria
     *  if CovLimit >= 80% of BuildingReplacement Coverage and ValuationMethod is ActualCashValue
     *      display - info message with 'meets' criteria
     *  if valuation method is not "Actual Cash"
     *      don't show banner.
     *
     * @param {coverages} buildingCoverages
     * @returns {{textToChange: string, showBanner: boolean}|{textToChange: string, showBanner: boolean}}
     */
    infoBannerForBuildingBusinessPropertyCoverage(buildingCoverages) {
        const ret = {
            showBanner: true,
            textToChange: 'does not meet'
        };
        const BP_COV_PUBLIC_ID = 'CPBPPCov';
        const BP_COV_TERM_RCA_PUBLIC_ID = 'zd2hgl7ijbuss8j2o0b7d68v7ba';
        const BP_COV_TERM_LIMIT_PUBLIC_ID = 'zpegkobadgu8m1mfebia7i6ngva';
        const BP_COV_TERM_VALUATION_METHOD_PUBLIC_ID = 'CPBPPValuationMethod';
        const BP_COV_TERM_VALUATION_METHOD_OPTION_ACTUAL_CASH_CODE = 'ActualCash';

        // Init helper with coverages
        const twiaCoveragesHelper = TWIACoveragesHelper(buildingCoverages);

        // Check valuation method first
        const valuationMethodTerm = twiaCoveragesHelper.getCoverageTerm({
            coveragePublicID: BP_COV_PUBLIC_ID,
            covSelected: true,
            termPublicID: BP_COV_TERM_VALUATION_METHOD_PUBLIC_ID
        });
        if (valuationMethodTerm.chosenTerm !== BP_COV_TERM_VALUATION_METHOD_OPTION_ACTUAL_CASH_CODE) {
            return {...ret, showBanner: false};
        }

        // Check Limit & RCA if valuation method is Actual Cash Value
        const rcaTerm = twiaCoveragesHelper.getCoverageTerm({
            coveragePublicID: BP_COV_PUBLIC_ID,
            covSelected: true,
            termPublicID: BP_COV_TERM_RCA_PUBLIC_ID
        });
        const limitTerm = twiaCoveragesHelper.getCoverageTerm({
            coveragePublicID: BP_COV_PUBLIC_ID,
            covSelected: true,
            termPublicID: BP_COV_TERM_LIMIT_PUBLIC_ID
        });

        const doesNotMeet = limitTerm.directValue < Math.round(rcaTerm.directValue * 0.8);
        return {...ret, textToChange: doesNotMeet ? 'does not meet' : 'meets'};
    },

    /**
     * * US12611 - Commercial Renewal - Building Sub-flow - Coverages - TWCB
     * US12229 - Commercial Policy Change - Coverages
     * For BuildingCoverage
     *  if CovLimit < 80% of BuildingReplacement Coverage and ValuationMethod is ActualCashValue
     *      display - info message 'does not meet' criteria
     *  if CovLimit >= 80% of BuildingReplacement Coverage and ValuationMethod is ActualCashValue
     *      display - info message with 'meets' criteria
     *  if valuation method is not "Actual Cash"
     *      don't show banner.
     *
     * @param {coverages} buildingCoverages
     * @returns {{textToChange: string, showBanner: boolean}|{textToChange: string, showBanner: boolean}}
     */
    infoBannerForBuildingCoverage(buildingCoverages) {
        const ret = {
            showBanner: true,
            textToChange: 'does not meet'
        };
        const BC_COV_PUBLIC_ID = 'CPBldgCov';
        const BC_COV_TERM_RCA_PUBLIC_ID = 'zp2gc9babmt282dtirsq1fpbtoa';
        const BC_COV_TERM_LIMIT_PUBLIC_ID = 'CPBldgCovLimit';
        const BC_COV_TERM_VALUATION_METHOD_PUBLIC_ID = 'CPBldgCovValuationMethod';
        const BC_COV_TERM_VALUATION_METHOD_OPTION_ACTUAL_CASH_CODE = 'ActualCash';

        // Init helper with coverages
        const twiaCoveragesHelper = TWIACoveragesHelper(buildingCoverages);

        // Check valuation method first
        const valuationMethodTerm = twiaCoveragesHelper.getCoverageTerm({
            coveragePublicID: BC_COV_PUBLIC_ID,
            covSelected: true,
            termPublicID: BC_COV_TERM_VALUATION_METHOD_PUBLIC_ID
        });
        if (valuationMethodTerm.chosenTerm !== BC_COV_TERM_VALUATION_METHOD_OPTION_ACTUAL_CASH_CODE) {
            return {...ret, showBanner: false};
        }

        // Check Limit & RCA if valuation method is Actual Cash Value
        const rcaTerm = twiaCoveragesHelper.getCoverageTerm({
            coveragePublicID: BC_COV_PUBLIC_ID,
            covSelected: true,
            termPublicID: BC_COV_TERM_RCA_PUBLIC_ID
        });
        const limitTerm = twiaCoveragesHelper.getCoverageTerm({
            coveragePublicID: BC_COV_PUBLIC_ID,
            covSelected: true,
            termPublicID: BC_COV_TERM_LIMIT_PUBLIC_ID
        });

        // return limitTerm.directValue < Math.round(rcaTerm.directValue * 0.8);

        const doesNotMeet = limitTerm.directValue < Math.round(rcaTerm.directValue * 0.8);
        return {...ret, textToChange: doesNotMeet ? 'does not meet' : 'meets'};
    },

};
